import {Input, Spin} from 'antd'
import classNames from 'classnames'
import {createValue} from 'components/form/utils'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NullAsDefault, renderSelf} from 'views/Shared'
import {lookup_lookupInfoByValue_Api} from '../../apis'
import useAsyncAction from '../../modules/asyncCache/useAsyncAction'
import {LayoutContext} from '../layouts/Default/LayoutContext'
import {FaAddressCard} from 'react-icons/fa6'
import {HiCheckCircle, HiXCircle} from 'react-icons/hi'

const LOOKUP_DEBOUNCE = 600

const IdentifierCodeLookupField = ({
                                       name,
                                       auto = false,
                                       handleApply,
                                       defaultValue,
                                       onChange = NullAsDefault,
                                       disabled = false,
                                       getValue = renderSelf,
                                       placeholder = 'ex: identifier code',
                                       className,
                                       params,
                                   }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const [fieldValue, setFieldValue] = useState(defaultValue ?? '')
    const [validInfo, setValidInfo] = useState(null)
    const [isLooking, setIsLooking] = useState(false)

    const debounceRef = useRef(null)
    const lastLookedUp = useRef(null)

    const {
        response,
        success,
        handleAsyncAction: lookupIdentifierCodeGetInfo,
    } = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
        values: {owner_type: params?.owner_type, owner_id: params?.owner_id},
        query: {
            search_type: params?.search_type ?? 'user',
            input_type: params?.input_type ?? 'identifier_code',
        },
    })

    const lookupRef = useRef(lookupIdentifierCodeGetInfo)
    const handleApplyRef = useRef(handleApply)
    const onChangeRef = useRef(onChange)
    useEffect(() => { lookupRef.current = lookupIdentifierCodeGetInfo }, [lookupIdentifierCodeGetInfo])
    useEffect(() => { handleApplyRef.current = handleApply }, [handleApply])
    useEffect(() => { onChangeRef.current = onChange }, [onChange])

    useEffect(() => {
        setIsLooking(false)
        const currentValue = lastLookedUp.current
        if (success) {
            const userInfo = _.get(response, 'data', {})
            setValidInfo(userInfo)
            handleApplyRef.current({...userInfo, identifier_code: currentValue})
        } else {
            setValidInfo({})
            handleApplyRef.current({identifier_code: currentValue})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, response])

    useEffect(() => {
        if (defaultValue) {
            lastLookedUp.current = defaultValue
            lookupRef.current({}, {input_value: defaultValue})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])  // chỉ chạy lúc mount

    const triggerLookup = useCallback((value) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (!value) {
            setValidInfo(null)
            setIsLooking(false)
            return
        }
        setIsLooking(true)
        debounceRef.current = setTimeout(() => {
            lastLookedUp.current = value
            lookupRef.current({}, {input_value: value})
        }, LOOKUP_DEBOUNCE)
    }, [])

    const handleChange = useCallback((e) => {
        const value = e?.target?.value ?? e
        setFieldValue(value)
        setValidInfo(null)
        onChangeRef.current(createValue(name, value))
        triggerLookup(value)
    }, [name, triggerLookup])

    const suffix = useMemo(() => {
        if (isLooking) return <Spin size="small"/>
        if (!fieldValue) return null
        if (validInfo === null) return null
        if (validInfo?.id) return <HiCheckCircle size={18} className="text-green-500"/>
        return <HiXCircle size={18} className="text-orange-400"/>
    }, [isLooking, fieldValue, validInfo])

    const prefix = useMemo(() => (
        <FaAddressCard
            size={15}
            className={
                !fieldValue ? 'text-color-400'
                    : validInfo?.id ? 'text-green-500'
                    : validInfo !== null ? 'text-orange-400'
                        : 'text-color-400'
            }
        />
    ), [fieldValue, validInfo])

    const hintText = useMemo(() => {
        if (!fieldValue) {
            return <span className="text-xs text-color-500 italic">{`* ${t('enter id code')}`}</span>
        }
        if (isLooking) {
            return <span className="text-xs text-color-500 italic">{t('searching')}...</span>
        }
        if (validInfo?.name) {
            return (
                <span className="space-x-1 text-xs">
                    <HiCheckCircle size={13} className="text-green-500 flex-shrink-0"/>
                    <span className="text-color-500 italic">{`* ${t('this identifier is linked to the account')} :`}</span>
                    <span className="font-semibold text-primary">{validInfo.name}</span>
                </span>
            )
        }
        if (validInfo !== null && !validInfo?.name) {
            return (
                <span className="space-x-1 text-xs text-color-500 italic">
                    <HiXCircle size={13} className="text-orange-400 flex-shrink-0"/>
                    {t('not found id code description')}
                </span>
            )
        }
        return null
    }, [fieldValue, isLooking, validInfo, t])

    return (
        <div className="flex flex-col gap-1">
            <Input
                name={name}
                prefix={prefix}
                suffix={suffix}
                allowClear={true}
                disabled={disabled}
                value={getValue(fieldValue)}
                onChange={handleChange}
                placeholder={_.isString(placeholder) ? t(placeholder) : undefined}
                className={classNames('w-full', className)}
            />
            {!!hintText && (
                <div className="px-1 min-h-4 transition-all duration-300">
                    {hintText}
                </div>
            )}
        </div>
    )
}

export default IdentifierCodeLookupField